import React from "react";
import { FlipHorizontal2, Flashlight, FlashlightOff, ZoomIn, ZoomOut } from "lucide-react";

/**
 * RecorderCameraOverlays — the in-preview UI chrome that sits on top of
 * the live <video> element: focus reticle, flip-camera + torch buttons,
 * and the zoom pill. Pure presentational; parent owns all state +
 * MediaStreamTrack capability detection.
 *
 * Renders nothing in the review phase — parent already short-circuits
 * via `recordPhase === "review"`, but the component is also a safe
 * no-op if no inputs are wired.
 *
 * Props:
 *   focusIndicator — { x, y } | null, drives the iOS-style focus reticle
 *   cameraReady    — bool, gates the flip/torch/zoom UI
 *   isRecording    — bool, hides flip-camera mid-record (track swap would
 *                    corrupt the file)
 *   torchSupported, torchOn, onToggleTorch — torch state + handler
 *   videoInputCount, facingMode, flipBusy, onFlipCamera — flip-camera
 *   zoomCaps       — { min, max, step } | null. When null, no zoom pill
 *   zoom           — current zoom level
 *   onApplyZoom    — (next: number) => void
 */
export const RecorderCameraOverlays = ({
  focusIndicator,
  cameraReady, isRecording,
  torchSupported, torchOn, onToggleTorch,
  videoInputCount, facingMode, flipBusy, onFlipCamera,
  zoomCaps, zoom, onApplyZoom,
}) => (
  <>
    {/* Focus indicator — small yellow square at the tap coordinates,
        fades out after 700ms. Same visual vocabulary as the iOS Camera
        app's focus reticle. */}
    {focusIndicator && (
      <div
        data-testid="focus-indicator"
        className="absolute pointer-events-none border-2 border-amber-300 rounded-sm animate-pulse"
        style={{
          left: `${focusIndicator.x - 24}px`,
          top: `${focusIndicator.y - 24}px`,
          width: 48,
          height: 48,
        }}
      />
    )}

    {/* Flip-camera + Torch overlay — top-right of the preview. Hidden
        during recording (track-swap mid-record would corrupt the file)
        and on single-camera devices for flip. Torch only renders when
        the camera reports torch capability (rear cam on iOS Safari
        17.4+ / Android Chrome 110+). */}
    {cameraReady && !isRecording && (
      <div className="absolute top-2 right-2 flex items-center gap-1.5">
        {torchSupported && (
          <button
            type="button"
            onClick={onToggleTorch}
            className={`flex items-center justify-center p-1.5 rounded-full backdrop-blur-sm border transition-all ${
              torchOn
                ? "bg-amber-400/90 border-amber-200 text-gray-900"
                : "bg-black/60 hover:bg-black/80 border-white/20 text-white"
            }`}
            data-testid="torch-toggle-btn"
            aria-label={torchOn ? "Turn flashlight off" : "Turn flashlight on"}
            aria-pressed={torchOn}
            title={torchOn ? "Turn flashlight off" : "Turn flashlight on"}
          >
            {torchOn ? <Flashlight className="h-3.5 w-3.5" /> : <FlashlightOff className="h-3.5 w-3.5" />}
          </button>
        )}
        {videoInputCount > 1 && (
          <button
            type="button"
            onClick={onFlipCamera}
            disabled={flipBusy}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-sm text-white text-[11px] font-medium border border-white/20 disabled:opacity-50 transition-all"
            data-testid="flip-camera-btn"
            aria-label="Switch camera direction"
            title={facingMode === "environment" ? "Switch to selfie camera" : "Switch to back camera"}
          >
            <FlipHorizontal2 className="h-3.5 w-3.5" />
            <span>{facingMode === "environment" ? "Back" : "Front"}</span>
          </button>
        )}
      </div>
    )}

    {/* Zoom pill — bottom-center of the preview. Only renders when the
        camera reports zoom capability (iOS Safari 17+, modern Android
        Chrome, current Edge). On unsupported browsers users still get
        the digital pinch-to-zoom that iOS/Android offer natively on
        the <video> element. */}
    {cameraReady && zoomCaps && (
      <div
        className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-2 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-white/20"
        data-testid="zoom-control"
      >
        <button
          type="button"
          onClick={() => onApplyZoom(Math.max(zoomCaps.min, zoom - (zoomCaps.step || 0.1)))}
          disabled={zoom <= zoomCaps.min}
          className="text-white/90 hover:text-white disabled:opacity-40 p-0.5"
          data-testid="zoom-out-btn"
          aria-label="Zoom out"
        >
          <ZoomOut className="h-3.5 w-3.5" />
        </button>
        <input
          type="range"
          min={zoomCaps.min}
          max={zoomCaps.max}
          step={zoomCaps.step || 0.1}
          value={zoom}
          onChange={(e) => onApplyZoom(parseFloat(e.target.value))}
          className="w-24 sm:w-32 accent-emerald-400 cursor-pointer"
          data-testid="zoom-slider"
          aria-label="Zoom level"
        />
        <button
          type="button"
          onClick={() => onApplyZoom(Math.min(zoomCaps.max, zoom + (zoomCaps.step || 0.1)))}
          disabled={zoom >= zoomCaps.max}
          className="text-white/90 hover:text-white disabled:opacity-40 p-0.5"
          data-testid="zoom-in-btn"
          aria-label="Zoom in"
        >
          <ZoomIn className="h-3.5 w-3.5" />
        </button>
        <span className="text-white/80 text-[10px] tabular-nums w-7 text-right" data-testid="zoom-level">
          {zoom.toFixed(1)}×
        </span>
      </div>
    )}
  </>
);
